/* ------------------------------------------------------------------ */
/*  API client for the Botilia Search backend                         */
/* ------------------------------------------------------------------ */

import type {
  SearchParams,
  SearchResponse,
  DefaultsResponse,
  AdminConfig,
  Analytics,
  LayoutConfig,
  SystemStatus,
  FeedConfig,
  FeedWithSchedulerStatus,
  SyncHistoryEntry,
  Facets,
  FilterFacetConfig,
  SemanticSearchStatus,
  SemanticSearchConfig,
  AIContextConfig,
  AutocompleteKeyword,
  CollectionCategory,
} from './types';

/** Resolve the API base URL lazily so widget.tsx init() can set it at runtime. */
export function getApiBase(): string {
  // Runtime override takes priority (set by widget.tsx from data-api-url attribute)
  // so that the embeddable widget can point to a different search server.
  // VITE_API_URL is baked in at build time and would otherwise always win.
  const runtimeUrl = typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).__BOTILIA_API_URL__ as string;
  return (
    runtimeUrl ||
    import.meta.env.VITE_API_URL ||
    '/api'
  );
}

/* ------------------------------------------------------------------ */
/*  Multi-tenant API Configuration                                     */
/* ------------------------------------------------------------------ */

/**
 * Configuration for the API client.
 * Set the API key for multi-tenant mode.
 */
interface ApiConfig {
  /** Public API key (pk_live_xxx) for tenant authentication */
  apiKey?: string;
}

let _apiConfig: ApiConfig = {};

/**
 * Configure the API client with tenant credentials.
 * Call this before making any API requests.
 */
export function configureApi(config: ApiConfig): void {
  _apiConfig = { ...config };
}

/**
 * Get the current API configuration.
 */
export function getApiConfig(): ApiConfig {
  return { ..._apiConfig };
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const DEFAULT_TIMEOUT_MS = 8000;
const MAX_RETRIES = 1;

async function request<T>(
  path: string,
  options: RequestInit & { signal?: AbortSignal } = {},
  retries: number = MAX_RETRIES,
): Promise<T> {
  const url = `${getApiBase()}${path}`;

  // Combine user signal with timeout
  let signal = options.signal;
  if (!signal) {
    signal = AbortSignal.timeout(DEFAULT_TIMEOUT_MS);
  }

  try {
    // Build headers - merge Content-Type with any custom headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) ?? {}),
    };

    // Add tenant API key if configured (for multi-tenant mode)
    if (_apiConfig.apiKey) {
      headers['x-tenant-api-key'] = _apiConfig.apiKey;
    }

    // Build fetch options without spreading headers twice
    const { headers: _ignored, ...restOptions } = options;

    const res = await fetch(url, {
      ...restOptions,
      headers,
      signal,
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(body || `HTTP ${res.status}`);
    }

    return (await res.json()) as T;
  } catch (err) {
    // Retry once on network errors (not aborts)
    if (
      retries > 0 &&
      err instanceof TypeError &&
      !(err instanceof DOMException)
    ) {
      return request<T>(path, options, retries - 1);
    }
    throw err;
  }
}

function adminHeaders(secret: string): Record<string, string> {
  return { 'x-admin-secret': secret };
}

/* ------------------------------------------------------------------ */
/*  Public Search API                                                  */
/* ------------------------------------------------------------------ */

/**
 * Full search across products, articles, and pages.
 */
export async function searchProducts(
  params: SearchParams,
): Promise<SearchResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.category) qs.set('category', params.category);
  if (params.type) qs.set('type', params.type);
  if (params.country) qs.set('country', params.country);
  if (params.region) qs.set('region', params.region);
  if (params.grape) qs.set('grape', params.grape);
  if (params.producer) qs.set('producer', params.producer);
  if (params.subcategory) qs.set('subcategory', params.subcategory);
  if (params.vintage) qs.set('vintage', params.vintage);
  if (params.tags) qs.set('tags', params.tags);
  if (params.body) qs.set('body', params.body);
  if (params.acidity) qs.set('acidity', params.acidity);
  if (params.tannins) qs.set('tannins', params.tannins);
  if (params.fruitiness) qs.set('fruitiness', params.fruitiness);
  if (params.minPrice !== undefined)
    qs.set('priceMin', String(params.minPrice));
  if (params.maxPrice !== undefined)
    qs.set('priceMax', String(params.maxPrice));
  if (params.inStock !== undefined) qs.set('inStock', String(params.inStock));
  if (params.sort) qs.set('sort', params.sort);
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.sessionId) qs.set('sessionId', params.sessionId);
  if (params.lang) qs.set('lang', params.lang);

  return request<SearchResponse>(`/search?${qs.toString()}`);
}

/**
 * Send a product click event for session-based personalization.
 * Fire-and-forget — errors are silently ignored.
 */
export function trackSessionClick(data: {
  sessionId: string;
  productId: string;
  category?: string;
  producer?: string;
  region?: string;
  price?: number;
  /** Search query + 0-based position that produced this click (for analytics CTR). */
  query?: string;
  position?: number;
  lang?: string;
}): void {
  fetch(`${getApiBase()}/session/click`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    keepalive: true,
  }).catch(() => { /* fire-and-forget */ });
}

/**
 * Autocomplete suggestions for a partial query.
 */
export async function getAutocomplete(
  query: string,
  limit = 7,
  signal?: AbortSignal,
  lang?: string,
): Promise<string[]> {
  const qs = new URLSearchParams({ q: query, limit: String(limit) });
  if (lang) qs.set('lang', lang);
  const data = await request<{ suggestions: string[]; queryTime: number }>(
    `/autocomplete?${qs.toString()}`,
    { signal },
  );
  return data.suggestions;
}

/**
 * Default content to show when no search query is active.
 * Returns best sellers, promo products, and popular searches.
 */
export async function getDefaults(lang?: string): Promise<DefaultsResponse> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return request<DefaultsResponse>(`/defaults${qs}`);
}

/**
 * Popular search suggestions (used in no-results and empty states).
 */
export async function getSuggestions(lang?: string): Promise<string[]> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  const data = await request<{ suggestions: string[] }>(`/suggestions${qs}`);
  return data.suggestions;
}

/* ------------------------------------------------------------------ */
/*  Cached banner fetch (shared by BannerWidget + InlineBannerWidget)  */
/* ------------------------------------------------------------------ */

let _bannerCache: { data: any[]; ts: number; promise?: Promise<any[]> } | null = null;
const BANNER_CACHE_TTL = 5 * 60_000;

export async function getCachedBanners(): Promise<any[]> {
  if (_bannerCache) {
    if (_bannerCache.promise) return _bannerCache.promise;
    if (Date.now() - _bannerCache.ts < BANNER_CACHE_TTL) return _bannerCache.data;
  }
  const promise = fetch(`${getApiBase()}/marketing/banners`, {
    signal: AbortSignal.timeout(8_000),
  })
    .then((r) => (r.ok ? r.json() : []))
    .then((d) => {
      const banners = Array.isArray(d) ? d : (d.banners ?? []);
      return banners;
    });
  _bannerCache = { data: [], ts: 0, promise };
  try {
    const data = await promise;
    _bannerCache = { data, ts: Date.now() };
    return data;
  } catch {
    _bannerCache = null;
    return [];
  }
}

/* ------------------------------------------------------------------ */
/*  Admin API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Fetch the full admin configuration.
 */
export async function getAdminConfig(
  secret: string,
  lang?: string,
): Promise<AdminConfig> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return request<AdminConfig>(`/admin/config${qs}`, {
    headers: adminHeaders(secret),
  });
}

/**
 * Update the admin configuration.
 */
export async function updateAdminConfig(
  secret: string,
  config: Partial<AdminConfig>,
  lang?: string,
): Promise<void> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  await request<void>(`/admin/config${qs}`, {
    method: 'PUT',
    headers: adminHeaders(secret),
    body: JSON.stringify(config),
  });
}

/**
 * Trigger a product/article sync from the Botilia API.
 */
export async function triggerSync(secret: string): Promise<void> {
  await request<void>('/admin/sync', {
    method: 'POST',
    headers: adminHeaders(secret),
  });
}

/**
 * Trigger a full site crawl.
 */
export async function triggerCrawl(secret: string): Promise<void> {
  await request<void>('/admin/crawl', {
    method: 'POST',
    headers: adminHeaders(secret),
  });
}

/**
 * Fetch search analytics data.
 */
export async function getAnalytics(
  secret: string,
  lang?: string,
): Promise<Analytics> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return request<Analytics>(`/admin/analytics${qs}`, {
    headers: adminHeaders(secret),
  });
}

/**
 * Fetch system health and status information.
 */
export async function getSystemStatus(
  secret: string,
): Promise<SystemStatus> {
  return request<SystemStatus>('/admin/status', {
    headers: adminHeaders(secret),
  });
}

/* ------------------------------------------------------------------ */
/*  Feed Management API                                                */
/* ------------------------------------------------------------------ */

/**
 * Fetch all feed configurations.
 */
export async function getFeeds(
  secret: string,
  lang?: string,
): Promise<{ feeds: FeedWithSchedulerStatus[]; total: number }> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return request<{ feeds: FeedWithSchedulerStatus[]; total: number }>(`/admin/feeds${qs}`, {
    headers: adminHeaders(secret),
  });
}

/**
 * Create a new feed configuration.
 */
export async function createFeed(
  secret: string,
  feed: Omit<FeedConfig, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<{ id: string; config: FeedConfig }> {
  return request<{ id: string; config: FeedConfig }>('/admin/feeds', {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify(feed),
  });
}

/**
 * Update an existing feed configuration.
 */
export async function updateFeed(
  secret: string,
  id: string,
  updates: Partial<FeedConfig>,
): Promise<{ config: FeedConfig }> {
  return request<{ config: FeedConfig }>(`/admin/feeds/${id}`, {
    method: 'PUT',
    headers: adminHeaders(secret),
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a feed configuration.
 */
export async function deleteFeed(
  secret: string,
  id: string,
): Promise<void> {
  await request<void>(`/admin/feeds/${id}`, {
    method: 'DELETE',
    headers: adminHeaders(secret),
  });
}

/**
 * Trigger a manual sync for a feed.
 * Uses a longer timeout (120s) since syncing large feeds can take time.
 */
export async function syncFeed(
  secret: string,
  id: string,
): Promise<{ stats: { total: number; parsed: number; skipped: number; errors: number }; productsImported: number }> {
  return request<{ stats: { total: number; parsed: number; skipped: number; errors: number }; productsImported: number }>(`/admin/feeds/${id}/sync`, {
    method: 'POST',
    headers: adminHeaders(secret),
    signal: AbortSignal.timeout(120_000), // 2 minutes for large feed syncs
  });
}

/**
 * Get sync history for a feed.
 */
export async function getFeedHistory(
  secret: string,
  id: string,
  limit = 50,
): Promise<{ history: SyncHistoryEntry[] }> {
  return request<{ history: SyncHistoryEntry[] }>(`/admin/feeds/${id}/history?limit=${limit}`, {
    headers: adminHeaders(secret),
  });
}

/**
 * Extract available field paths from a feed URL.
 * Uses a longer timeout (60s) since large feeds take time to fetch and parse.
 */
export async function extractFeedFields(
  secret: string,
  url: string,
  format?: string,
  auth?: { type: 'basic' | 'bearer' | 'apikey'; credentials: string },
): Promise<{ format: string; fields: string[]; sampleCount: number }> {
  return request<{ format: string; fields: string[]; sampleCount: number }>('/admin/feeds/extract-fields', {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ url, format, auth }),
    signal: AbortSignal.timeout(60_000), // 60 seconds for large feeds
  });
}

/**
 * Test a CSS selector against a URL.
 * Returns matching elements for preview.
 */
export async function testCrawlSelector(
  secret: string,
  url: string,
  selector: string,
  attribute?: string,
): Promise<{ matches: string[]; matchCount: number; error?: string }> {
  return request<{ matches: string[]; matchCount: number; error?: string }>('/admin/feeds/test-selector', {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ url, selector, attribute }),
    signal: AbortSignal.timeout(30_000),
  });
}

/* ------------------------------------------------------------------ */
/*  Layout Config                                                       */
/* ------------------------------------------------------------------ */

/**
 * Fetch the saved widget layout configuration.
 * Returns null when no config has been saved yet (use client-side default).
 */
export async function getLayoutConfig(secret: string, lang?: string): Promise<LayoutConfig | null> {
  try {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    return await request<LayoutConfig>(`/admin/layout${qs}`, {
      headers: adminHeaders(secret),
      signal: AbortSignal.timeout(10_000),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Persist the widget layout configuration to the backend.
 */
export async function updateLayoutConfig(secret: string, config: LayoutConfig, lang?: string): Promise<void> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  await request<{ ok: boolean }>(`/admin/layout${qs}`, {
    method: 'PUT',
    headers: adminHeaders(secret),
    body: JSON.stringify(config),
  });
}

/**
 * Fetch the public layout config (no admin secret required — for the widget).
 */
export async function getPublicLayoutConfig(lang?: string): Promise<LayoutConfig | null> {
  try {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    return await request<LayoutConfig>(`/layout${qs}`, {
      signal: AbortSignal.timeout(10_000),
    });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Filter facets
// ---------------------------------------------------------------------------

const _catalogFacetsCache = new Map<string, { data: Facets | null; ts: number; promise?: Promise<Facets | null> }>();
const CATALOG_FACETS_TTL = 5 * 60_000;

const _filterFacetsCache = new Map<string, { data: FilterFacetConfig[]; ts: number; promise?: Promise<FilterFacetConfig[]> }>();
const FILTER_FACETS_TTL = 5 * 60_000;

export async function getFilterFacets(secret: string, lang?: string): Promise<{ facets: FilterFacetConfig[] }> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return request<{ facets: FilterFacetConfig[] }>(`/admin/filter-facets${qs}`, {
    headers: adminHeaders(secret),
  });
}

export async function updateFilterFacets(secret: string, facets: FilterFacetConfig[], lang?: string): Promise<void> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  await request<{ ok: boolean }>(`/admin/filter-facets${qs}`, {
    method: 'PUT',
    headers: adminHeaders(secret),
    body: JSON.stringify(facets),
  });
  // Bust the public-config cache so the widget picks up the new order/visibility.
  _filterFacetsCache.clear();
}

export async function deleteFilterFacet(secret: string, id: string, lang?: string): Promise<void> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  await request<{ ok: boolean }>(`/admin/filter-facets/${encodeURIComponent(id)}${qs}`, {
    method: 'DELETE',
    headers: adminHeaders(secret),
  });
  _filterFacetsCache.clear();
}

export async function getPublicFilterFacets(lang?: string): Promise<FilterFacetConfig[]> {
  const key = lang ?? '__default__';
  const cached = _filterFacetsCache.get(key);

  if (cached?.promise) return cached.promise;
  if (cached && Date.now() - cached.ts < FILTER_FACETS_TTL) return cached.data;

  const promise = (async () => {
    try {
      const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
      const data = await request<FilterFacetConfig[]>(`/filter-facets${qs}`, {
        signal: AbortSignal.timeout(10_000),
      });
      _filterFacetsCache.set(key, { data, ts: Date.now() });
      return data;
    } catch {
      _filterFacetsCache.delete(key);
      return [];
    }
  })();

  _filterFacetsCache.set(key, { ...(cached ?? { data: [], ts: 0 }), promise });
  promise.finally(() => {
    const entry = _filterFacetsCache.get(key);
    if (entry) {
      delete entry.promise;
    }
  });

  return promise;
}

export type { CollectionCategory } from './types';

/** A curated main category for the sidebar (value = the feed's category, label = display override). */
export interface MainCategory { value: string; label?: string }

/** Admin: the curated main categories + every category in the catalogue with counts. */
export async function getMainCategories(secret: string, lang?: string): Promise<{
  selected: MainCategory[];
  available: Array<{ value: string; count: number }>;
}> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return request(`/admin/main-categories${qs}`, { headers: adminHeaders(secret) });
}

/** Admin: replace the curated main categories (order matters). */
export async function updateMainCategories(secret: string, categories: MainCategory[], lang?: string): Promise<void> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  await request<{ ok: boolean }>(`/admin/main-categories${qs}`, {
    method: 'PUT',
    headers: adminHeaders(secret),
    body: JSON.stringify({ categories }),
  });
  // Sidebar facets come from the search response / catalog facets — drop those caches.
  _catalogFacetsCache.clear();
}

/** Admin: all imported collections + which ids are excluded from public surfaces. */
export async function getAdminCategories(secret: string, lang?: string): Promise<{ categories: CollectionCategory[]; excludedIds: string[] }> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return request<{ categories: CollectionCategory[]; excludedIds: string[] }>(`/admin/categories${qs}`, {
    headers: adminHeaders(secret),
  });
}

/** Admin: replace the excluded-collection id set. */
export async function updateCategoryExclusions(secret: string, excludedIds: string[]): Promise<void> {
  await request<{ ok: boolean }>('/admin/categories/exclusions', {
    method: 'PUT',
    headers: adminHeaders(secret),
    body: JSON.stringify({ excludedIds }),
  });
  // Bust the public cache so the widget reflects exclusions promptly.
  _categoriesCache.clear();
}


const _categoriesCache = new Map<string, { data: CollectionCategory[]; ts: number; promise?: Promise<CollectionCategory[]> }>();
const CATEGORIES_TTL = 5 * 60_000;

/** Fetch curated collections (crawled categories) for a language — cached 5 min, [] on error. */
export async function getPublicCategories(lang?: string): Promise<CollectionCategory[]> {
  const key = lang ?? '__default__';
  const cached = _categoriesCache.get(key);
  if (cached?.promise) return cached.promise;
  if (cached && Date.now() - cached.ts < CATEGORIES_TTL) return cached.data;

  const promise = (async () => {
    try {
      const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
      const data = await request<CollectionCategory[]>(`/categories${qs}`, {
        signal: AbortSignal.timeout(10_000),
      });
      _categoriesCache.set(key, { data, ts: Date.now() });
      return data;
    } catch {
      return cached?.data ?? [];
    }
  })();

  _categoriesCache.set(key, { ...(cached ?? { data: [], ts: 0 }), promise });
  promise.finally(() => {
    const entry = _categoriesCache.get(key);
    if (entry) delete entry.promise;
  });
  return promise;
}

/**
 * Fetch facet counts computed from ALL products in the catalog.
 * Used by the FiltersWidget to show filter options with counts.
 */
export async function getCatalogFacets(lang?: string): Promise<Facets | null> {
  const key = lang ?? '__default__';
  const cached = _catalogFacetsCache.get(key);

  if (cached?.promise) return cached.promise;
  if (cached && Date.now() - cached.ts < CATALOG_FACETS_TTL) return cached.data;

  const promise = (async () => {
    try {
      const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
      const data = await request<Facets>(`/catalog-facets${qs}`, {
        signal: AbortSignal.timeout(10_000),
      });
      _catalogFacetsCache.set(key, { data, ts: Date.now() });
      return data;
    } catch {
      _catalogFacetsCache.delete(key);
      return null;
    }
  })();

  _catalogFacetsCache.set(key, { ...(cached ?? { data: null, ts: 0 }), promise });
  promise.finally(() => {
    const entry = _catalogFacetsCache.get(key);
    if (entry) {
      delete entry.promise;
    }
  });

  return promise;
}

/* ------------------------------------------------------------------ */
/*  Semantic / AI Search                                               */
/* ------------------------------------------------------------------ */

/**
 * Fetch the current semantic search status (index stats, config, API key).
 */
export async function getSemanticSearchStatus(
  secret: string,
): Promise<SemanticSearchStatus> {
  return request<SemanticSearchStatus>('/admin/semantic-search/status', {
    headers: adminHeaders(secret),
  });
}

/**
 * Update semantic search configuration (partial merge).
 */
export async function updateSemanticSearchConfig(
  secret: string,
  config: Partial<SemanticSearchConfig>,
): Promise<void> {
  await request<{ message: string }>('/admin/semantic-search/config', {
    method: 'PUT',
    headers: adminHeaders(secret),
    body: JSON.stringify(config),
  });
}

/**
 * Get AI context configuration (tone, response style, cache settings).
 */
export async function getAIContextConfig(
  secret: string,
): Promise<AIContextConfig> {
  const data = await request<{ config: AIContextConfig }>('/admin/ai-context/config', {
    headers: adminHeaders(secret),
  });
  return data.config;
}

/**
 * Update AI context configuration (partial merge).
 */
export async function updateAIContextConfig(
  secret: string,
  config: Partial<AIContextConfig>,
): Promise<void> {
  await request<{ message: string }>('/admin/ai-context/config', {
    method: 'PUT',
    headers: { ...adminHeaders(secret), 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
}

/**
 * Clear the AI context cache.
 */
export async function clearAIContextCache(
  secret: string,
): Promise<{ deleted: number }> {
  return request<{ message: string; deleted: number }>('/admin/ai-context/clear', {
    method: 'POST',
    headers: adminHeaders(secret),
  });
}

/**
 * Trigger a full embedding reindex. Returns immediately; reindex runs async.
 */
export async function triggerEmbeddingReindex(
  secret: string,
): Promise<{ message: string; totalProducts: number }> {
  return request<{ message: string; totalProducts: number }>('/admin/semantic-search/reindex', {
    method: 'POST',
    headers: adminHeaders(secret),
  });
}

/* ------------------------------------------------------------------ */
/*  Autocomplete Keywords                                              */
/* ------------------------------------------------------------------ */

export async function getAutocompleteKeywords(
  secret: string,
): Promise<AutocompleteKeyword[]> {
  const data = await request<{ keywords: AutocompleteKeyword[] }>('/admin/autocomplete/keywords', {
    headers: adminHeaders(secret),
  });
  return data.keywords;
}

export async function addAutocompleteKeyword(
  secret: string,
  keyword: string,
  boost: number = 10,
): Promise<void> {
  await request('/admin/autocomplete/keywords', {
    method: 'POST',
    headers: { ...adminHeaders(secret), 'Content-Type': 'application/json' },
    body: JSON.stringify({ keyword, boost }),
  });
}

export async function updateAutocompleteKeyword(
  secret: string,
  keyword: string,
  updates: { boost?: number; enabled?: boolean },
): Promise<void> {
  await request(`/admin/autocomplete/keywords/${encodeURIComponent(keyword)}`, {
    method: 'PUT',
    headers: { ...adminHeaders(secret), 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function deleteAutocompleteKeyword(
  secret: string,
  keyword: string,
): Promise<void> {
  await request(`/admin/autocomplete/keywords/${encodeURIComponent(keyword)}`, {
    method: 'DELETE',
    headers: adminHeaders(secret),
  });
}

export async function triggerAutocompleteReindex(
  secret: string,
): Promise<void> {
  await request('/admin/autocomplete/reindex', {
    method: 'POST',
    headers: adminHeaders(secret),
  });
}
